
import React from 'react';
import { SimulationConfig } from '../../../types';
import { Toggle } from '../../../Toggle';

interface MultiSimControlsProps {
    config: SimulationConfig;
    setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
    editingSimId: string | null;
    setEditingSimId: (id: string | null) => void;
    tracks: any[];
}

export const MultiSimControls: React.FC<MultiSimControlsProps> = ({ config, setConfig, editingSimId, setEditingSimId, tracks }) => {
    return (
        <div className="mt-8 pt-6 border-t border-white/10 space-y-6">
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <h4 className="text-sm font-bold text-white">Multi-Simulation Mode</h4>
                    <p className="text-[10px] text-white/50">Play tracks in separate physics simulations</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setConfig(p => ({
                            ...p,
                            masterSpeed: 1.0,
                            rhythmicTimeScale: 1.0,
                            rhythmicTimeScaleRate: 0,
                            speedIncreaseRate: 0,
                            speedIncreaseMode: 'STATIC',
                            rhythmicTimeScaleIncreaseMode: 'NONE'
                        }))}
                        className="px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded text-[9px] font-bold uppercase tracking-widest hover:bg-red-500/30 transition-all"
                    >
                        Reset Speed
                    </button>
                    <Toggle 
                        active={config.rhythmicMultiSimEnabled} 
                        onClick={() => setConfig({ ...config, rhythmicMultiSimEnabled: !config.rhythmicMultiSimEnabled })} 
                    />
                </div>
            </div>

            {config.rhythmicMultiSimEnabled && (
                <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <h4 className="text-xs font-bold text-white">Track Intro Mode</h4>
                            <p className="text-[10px] text-white/50">Simulations appear as their tracks start</p>
                        </div>
                        <Toggle 
                            active={!!config.rhythmicMultiSimIntroMode} 
                            onClick={() => setConfig({ ...config, rhythmicMultiSimIntroMode: config.rhythmicMultiSimIntroMode ? false : 'SEQUENTIAL' })} 
                        />
                    </div>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h4 className="text-xs font-bold text-white uppercase tracking-widest">Simulations</h4>
                            <button
                                onClick={() => {
                                    const newSim = { id: Math.random().toString(), name: `Sim ${config.rhythmicSimulations.length + 1}`, trackIndices: [] };
                                    setConfig({ ...config, rhythmicSimulations: [...config.rhythmicSimulations, newSim] });
                                }}
                                className="px-2 py-1 bg-white/10 hover:bg-white/20 rounded text-[10px] text-white transition-colors"
                            >
                                + Add Simulation
                            </button>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                            {config.rhythmicSimulations.map((sim, simIdx) => (
                                <div key={sim.id} className="relative group">
                                    <div className={`p-4 bg-white/5 rounded-2xl border transition-all duration-300 ${editingSimId === sim.id ? 'border-blue-500 bg-blue-500/10 ring-4 ring-blue-500/10' : 'border-white/10 hover:border-white/20 hover:bg-white/[0.07]'}`}>
                                        <div className="flex items-center justify-between gap-2 mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-black ${editingSimId === sim.id ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/40'}`}>
                                                    {simIdx + 1}
                                                </div>
                                                <input
                                                    type="text"
                                                    value={sim.name}
                                                    onChange={(e) => {
                                                        const newSims = [...config.rhythmicSimulations];
                                                        newSims[simIdx] = { ...sim, name: e.target.value };
                                                        setConfig({ ...config, rhythmicSimulations: newSims });
                                                    }}
                                                    className="bg-transparent text-xs font-black text-white focus:outline-none border-b border-transparent focus:border-white/20 py-1"
                                                />
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => {
                                                        const nextId = editingSimId === sim.id ? null : sim.id;
                                                        setEditingSimId(nextId);
                                                    }}
                                                    className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${editingSimId === sim.id ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                                                >
                                                    {editingSimId === sim.id ? 'Close' : 'Customize'}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newSims = config.rhythmicSimulations.filter(s => s.id !== sim.id);
                                                        setConfig({ ...config, rhythmicSimulations: newSims });
                                                    }}
                                                    className="p-1.5 text-white/20 hover:text-red-400 rounded-lg transition-all"
                                                >
                                                    <i className="fa-solid fa-trash text-[10px]"></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <p className="text-[9px] text-white/30 uppercase tracking-[0.2em] font-black">Midi Tracks</p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {tracks.map(track => (
                                                        <button
                                                            key={track.index}
                                                            onClick={() => {
                                                                const newSims = [...config.rhythmicSimulations];
                                                                const currentTracks = sim.trackIndices || [];
                                                                const isAssigned = currentTracks.includes(track.index);
                                                                newSims[simIdx] = {
                                                                    ...sim,
                                                                    trackIndices: isAssigned 
                                                                        ? currentTracks.filter((id: number) => id !== track.index)
                                                                        : [...currentTracks, track.index]
                                                                };
                                                                setConfig({ ...config, rhythmicSimulations: newSims });
                                                            }}
                                                            className={`w-7 h-7 flex items-center justify-center rounded-lg text-[10px] font-black transition-all ${
                                                                (sim.trackIndices || []).includes(track.index)
                                                                    ? 'bg-blue-500 text-white shadow-[0_4px_12px_rgba(59,130,246,0.3)]'
                                                                    : 'bg-white/5 text-white/20 border border-white/5 hover:bg-white/10'
                                                            }`}
                                                        >
                                                            {track.index + 1}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
